// ==========================================
// ManageTickets.jsx (WITH FILTER SECTION)
// ==========================================

import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import { API_BASE_URL } from "../../config";
import { FaEye, FaUserEdit } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

/* -------------------------------------------------------
   POPUP (Ticket View + Assign)
-------------------------------------------------------- */

const TicketPopup = ({ ticketId, onClose, refresh, getAuth }) => {
  const [data, setData] = useState(null);
  const [assign, setAssign] = useState({
    asset_id: "",
    location_id: "",
    agent_id: "",
  });

  useEffect(() => {
    if (!ticketId) return;

    axios
      .get(`${API_BASE_URL}/tickets/${ticketId}/ticketDetails_test`, {
        headers: getAuth(),
      })
      .then((res) => {
        const response = res.data;
        setData(response);

        setAssign({
          asset_id: response.ticket.asset_id || "",
          location_id: response.ticket.location_id || "",
          agent_id: response.ticket.assigned_to || "",
        });
      })
      .catch(() => toast.error("Failed to load ticket"));
  }, [ticketId]);

  const saveAssign = () => {
    axios
      .post(`${API_BASE_URL}/tickets/${ticketId}/assign`, assign, {
        headers: getAuth(),
      })
      .then(() => {
        toast.success("Ticket updated!");
        refresh();
        onClose();
      })
      .catch(() => toast.error("Update failed"));
  };

  if (!data) return null;

  const t = data.ticket;

  return (
    <div className="modal fade show d-block" style={{ background: "#00000090" }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">

          <div className="modal-header">
            <h5>Ticket Information</h5>
            <button className="close" onClick={onClose}>×</button>
          </div>

          <div className="modal-body">
            <div className="row">

              {/* LEFT: DETAILS */}
              <div className="col-md-7">
                <table className="table table-bordered small">
                  <tbody>
                    <tr><th>Subject</th><td>{t.subject}</td></tr>
                    <tr><th>Code</th><td>{t.ticket_code}</td></tr>
                    <tr>
                      <th>Asset</th>
                      <td>
                        {t.asset_name} <br />
                        <span style={{ color: "green" }}>[{t.asset_code}]</span>
                      </td>
                    </tr>
                    <tr><th>Reported By</th><td>{data.reported_by}</td></tr>
                    <tr><th>Assigned To</th><td>{data.assigned_to}</td></tr>
                    <tr><th>Priority</th><td>{t.priority_title}</td></tr>
                    <tr><th>Status</th><td>{t.status_title}</td></tr>
                    <tr><th>Resolution</th><td>{t.res_title ?? "--"}</td></tr>
                    <tr><th>Date</th><td>{t.created_at}</td></tr>
                    <tr><th>Location</th><td>{data.location_info}</td></tr>
                    <tr><th>Description</th><td>{t.description}</td></tr>
                  </tbody>
                </table>
              </div>

              {/* RIGHT: ASSIGN */}
              <div className="col-md-5">

                <label>Assets</label>
                <select
                  className="form-control"
                  value={assign.asset_id}
                  onChange={(e) => setAssign({ ...assign, asset_id: e.target.value })}
                >
                  <option value="">Select Asset</option>
                  {data.assets.map((a) => (
                    <option key={a.assets_id} value={a.assets_id}>
                      [{a.company_asset_code}] {a.name}
                    </option>
                  ))}
                </select>

                <label className="mt-2">Location</label>
               <select
  className="form-control"
  value={assign.location_id}
  onChange={(e) =>
    setAssign({ ...assign, location_id: e.target.value })
  }
>
  <option value="">Select Location</option>
  {data.locations.map((loc) => (
    <option key={loc.loc_id} value={loc.loc_id}>
      {loc.block_name} / {loc.floor_name}{" "}
      {loc.ward_name ? `/ ${loc.ward_name}` : ""} / {loc.place_name}
    </option>
  ))}
</select>
                <label className="mt-2">Assigned To</label>
                <select
                  className="form-control"
                  value={assign.agent_id}
                  onChange={(e) => setAssign({ ...assign, agent_id: e.target.value })}
                >
                  <option value="">Select Employee</option>
                  {data.agents.map((ag) => (
                    <option key={ag.user_id} value={ag.user_id}>
                      [{ag.employee_id}] {ag.first_name} {ag.last_name}
                    </option>
                  ))}
                </select>

                <button className="btn btn-primary mt-3" onClick={saveAssign}>
                  Save
                </button>
              </div>

            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>

        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------
   MAIN PAGE — WITH FILTER BAR
-------------------------------------------------------- */

const ManageTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [pending, setPending] = useState(false);
  const [search, setSearch] = useState("");

  const [filters, setFilters] = useState({
    priority: 0,
    ticket_status: 0,
    helpdesk_id: 0,
  });

  const [dropdowns, setDropdowns] = useState({
    priority_list: [],
    status_list: [],
    helpdesk_departments: [],
  });

  const [ticketId, setTicketId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const getAuth = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  });

  // Load filter values
  const loadFilters = () => {
    axios
      .get(`${API_BASE_URL}/fil`, { headers: getAuth() })
      .then((res) => setDropdowns(res.data))
      .catch(() => toast.error("Unable to load filters"));
  };

  // Load tickets
  const loadTickets = () => {
    setPending(true);
    axios
      .post(`${API_BASE_URL}/tickets/list`, filters, {
        headers: getAuth(),
      })
      .then((res) => setTickets(res.data.data || []))
      .finally(() => setPending(false));
  };

  useEffect(() => {
    loadFilters();
    loadTickets();
  }, []);

  // Columns
  const columns = [
    {
      name: "Action",
      width: "160px",
      cell: (row) => (
        <>
        <button
  className="btn btn-sm"
  style={{
    marginRight: "8px",
    backgroundColor: "transparent",
    border: "none",
    padding: "4px",
    boxShadow: "none",
  }}
  onClick={() => (window.location.href = `/ticket-view/${row.ticket_id}`)}
>
  <FaEye size={16} className="text-info" />
</button>



          <button
  className="btn btn-sm"
  style={{
    backgroundColor: "transparent",
    border: "none",
    padding: "4px",
    boxShadow: "none",
  }}
  onClick={() => {
    setTicketId(row.ticket_id);
    setShowPopup(true);
  }}
>
  <FaUserEdit size={16} className="text-warning" />
</button>

        </>
      ),
    },
    { name: "Code", selector: (r) => r.ticket_code, sortable: true},
    { name: "Subject", selector: (r) => r.subject ,sortable: true},
    { name: "Department", selector: (r) => r.from_department,sortable: true },
    { name: "Helpdesk", selector: (r) => r.helpdesk_department ,sortable: true},
    { name: "Priority", selector: (r) => r.tick_priority,sortable: true },
    { name: "Status", selector: (r) => r.tick_status,sortable: true },
    { name: "Date", selector: (r) => r.created_at ,sortable: true},
    { name: "Due", selector: (r) => r.complete_by ,sortable: true},
  ];

  const filtered = tickets.filter((t) =>
    JSON.stringify(t).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="container mt-4">
      <ToastContainer />

      <h5>Ticket List</h5>

      {/* FILTER BAR */}
      <div className="card p-3">
        <div className="row">

          {/* Priority */}
          <div className="col-md-3">
            <label>Priority</label>
            <select
              className="form-control"
              value={filters.priority}
              onChange={(e) =>
                setFilters({ ...filters, priority: e.target.value })
              }
            >
              <option value="0">All</option>
              {dropdowns.priority_list.map((p) => (
                <option value={p.priority_id} key={p.priority_id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div className="col-md-3">
            <label>Status</label>
            <select
              className="form-control"
              value={filters.ticket_status}
              onChange={(e) =>
                setFilters({ ...filters, ticket_status: e.target.value })
              }
            >
              <option value="0">All</option>
              {dropdowns.status_list.map((s) => (
                <option value={s.status_id} key={s.status_id}>
                  {s.status_title}
                </option>
              ))}
            </select>
          </div>

          {/* Department */}
          <div className="col-md-3">
            <label>Department</label>
            <select
              className="form-control"
              value={filters.helpdesk_id}
              onChange={(e) =>
                setFilters({ ...filters, helpdesk_id: e.target.value })
              }
            >
              <option value="0">ALL</option>
              {dropdowns.helpdesk_departments.map((d) => (
                <option value={d.department_id} key={d.department_id}>
                  {d.department_name}
                </option>
              ))}
            </select>
          </div>

          {/* GET BUTTON */}
         <div className="col-md-3 d-flex align-items-end">
  
                  <button className="btn btn-primary w-100"  onClick={loadTickets}>Get</button>

</div>


        </div>
      </div>

      {/* Search bar */}
     {/* SEARCH BAR - RIGHT ALIGNED */}
<div className="d-flex justify-content-end my-3">
  <input
    type="text"
    className="form-control"
    placeholder="Search..."
    style={{ width: "250px" }}
    onChange={(e) => setSearch(e.target.value)}
  />
</div>


      {/* TABLE */}
      <DataTable
        columns={columns}
        data={filtered}
        progressPending={pending}
        pagination
        striped
      />

      {/* POPUP */}
      {showPopup && (
        <TicketPopup
          ticketId={ticketId}
          onClose={() => setShowPopup(false)}
          refresh={loadTickets}
          getAuth={getAuth}
        />
      )}
    </div>
  );
};

export default ManageTickets;
